import { viewPort } from '@lib/style/utils';
import { createContext, stateSetter } from '@packages/context';
import { createActions } from '@packages/context/lib/action';
import { Record } from 'immutable';
import { NONE, RESIZE_ACTION, UI_ACTION } from './type';

type state = {
    header: {
        elevation: number;
        visibility: boolean;
    };
    page: {
        drawer: boolean;
        hideDrawer: boolean;
        drawerAction: string;
        background: string;
        title: string;
    };
};

export const HEADER_ELEVATION = 0;
export const PAGE_BACKGROUND = '#eee';
export const DRAWER_BREAKPOINT = 1280;

const isMedical = /(sign-up\/medical|medical-vendor-signup)/i.test(
    typeof window !== 'undefined' ? window.location.href : '',
);

const initialState = Record<state>({
    header: {
        elevation: HEADER_ELEVATION,
        visibility: !isMedical,
    },

    page: {
        background: PAGE_BACKGROUND,
        drawer: false,
        hideDrawer: false,
        drawerAction: NONE,
        title: '',
    },
});

type ActionKeys =
    | 'headerElevation'
    | 'resetHeaderElevation'
    | 'pageBackground'
    | 'resetPageBackground'
    | 'setHeaderVisibility'
    | 'dynamicDrawer'
    | 'setDrawer'
    | 'setDrawerAction'
    | 'openDrawer'
    | 'setTitle'
    | 'closeDrawer'
    | 'toggleDrawer'
    | 'setDrawerHidden';

const actions = createActions<ActionKeys>([
    'headerElevation',
    'resetHeaderElevation',
    'pageBackground',
    'resetPageBackground',
    'setHeaderVisibility',
    'dynamicDrawer',
    'setDrawer',
    'setDrawerAction',
    'openDrawer',
    'setTitle',
    'closeDrawer',
    'toggleDrawer',
    'setDrawerHidden',
]);

const { Provider, Context } = createContext<state, typeof actions>({
    initialState,
    actions,
    name: 'Application',
    debug: false,
    events: {
        onHeaderElevation: stateSetter('header.elevation'),
        onSetHeaderVisibility: stateSetter('header.visibility'),
        onResetHeaderElevation: stateSetter('header.elevation', null, HEADER_ELEVATION),

        onSetTitle: stateSetter('page.title'),

        onPageBackground: stateSetter('page.background'),
        onResetPageBackground: stateSetter('page.background', null, PAGE_BACKGROUND),

        onSetDrawerAction: stateSetter('page.drawerAction'),

        onSetDrawerHidden: stateSetter('page.hideDrawer'),
        onSetDrawer: stateSetter('page.drawer'),

        onOpenDrawer: (state): any => state.setIn(['page', 'drawer'], true).setIn(['page', 'drawerAction'], UI_ACTION),

        onCloseDrawer: (state): any =>
            state.setIn(['page', 'drawer'], false).setIn(['page', 'drawerAction'], UI_ACTION),

        onToggleDrawer: (state): any =>
            state
                .setIn(['page', 'drawer'], !state.getIn(['page', 'drawer']))
                .setIn(['page', 'drawerAction'], UI_ACTION),

        onDynamicDrawer(state): any {
            if (state.getIn(['page', 'hideDrawer'])) {
                return state;
            }

            const { width } = viewPort();
            const drawerAction = state.getIn(['page', 'drawerAction']);

            const isMobile = width < DRAWER_BREAKPOINT;
            const isOpen = state.getIn(['page', 'drawer']);
            const isUIAction = drawerAction === UI_ACTION;

            // eslint-disable-next-line default-case
            switch (true) {
                case isMobile && isOpen && !isUIAction:
                    state = state.setIn(['page', 'drawer'], false);
                    break;
                case !isMobile && !isOpen && !isUIAction:
                    state = state.setIn(['page', 'drawer'], true);
                    break;
                default:
                    return state;
            }

            return state.setIn(['page', 'drawerAction'], RESIZE_ACTION);
        },
    },
});

export { Provider, Context };

export const ApplicationContext = Context;
